<template>
  <section
    class="flex items-center self-stretch bg-elev-header-bg border-b border-b-bor-nav pl-2.5 cursor-pointer"
    @click="onBarClick"
  >
    <div
      v-if="preview.thumbnailUrl"
      class="flex items-center justify-center py-1.5 pl-1.5"
    >
      <img
        class="h-10 w-10 rounded-4 object-cover"
        :src="preview.thumbnailUrl"
        alt=""
      >
    </div>
    <div class="flex min-w-0 flex-1 flex-col self-stretch p-1.5">
      <p class="h-5 truncate text-body-m-14-20 text-surf-brand-base-def">
        {{ pinnedMessageSenderName }}
      </p>
      <p class="flex min-w-0 items-center gap-1 text-body-r-14-20 text-fg-base-fg10">
        <wui-icon
          v-if="preview.icon"
          :name="preview.icon"
          class="h-5! w-5! shrink-0"
        />
        <span class="min-w-0 truncate first-letter:capitalize">{{ preview.text }}</span>
      </p>
    </div>
    <div
      v-if="sortedEvents.length > 1"
      class="flex h-full items-center justify-center text-body-r-14-20 text-fg-base-fg22"
    >
      {{ displayIndex }} / {{ sortedEvents.length }}
    </div>
    <wui-btn
      icon
      variant="neut"
      state="outline"
      class="h-13! w-13! shrink-0 text-fg-base-fg22!"
      :prepend-icon="sortedEvents.length > 1 ? 'listM' : 'pinOffM'"
      :title="sortedEvents.length > 1 ? $t('chat.pinnedMessagesList') : $t('common.unpinMessage')"
      @click.stop="onActionClick"
    />
  </section>
</template>

<script setup lang="ts">
import { WuiBtn, WuiIcon } from '@wui/common-library'
import { MessageIncomingEvent, MessageOutgoingEvent, PinnedMessage, PServed } from '@wui/im'
import { computed, onBeforeUnmount, PropType, ref, watch } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'
import {
  getCachedVoiceDurationSec,
  getVoiceMessagePeaks,
} from '@/entities/voice-message'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { GUEST, PSTNGUEST } from '@/shared/utils/constants'

import { getPinnedPreview, getPinnedVoiceFileCookie } from '../lib/get-pinned-preview'
import { sortPinnedNewestFirst } from '../lib/sort-pinned-messages'

const props = defineProps({
  events: {
    type: Object as PropType<PinnedMessage[]>,
    required: true,
  },
  pServed: {
    type: String as PropType<PServed>,
    default: '',
  },
})

const emit = defineEmits<{
  (event: 'deletePinEvent', value: number): void
  (event: 'openList', value: number): void
  (event: 'goToMessage', value: number): void
}>()

const { findByPServed } = useStoreContacts()
const { findByPServed: findByPServedRoom, findGuestByPserved } = useStoreRooms()
const { currentUser } = useCurrentUser()
const { t } = useLocalization()

const sortedEvents = computed(() => sortPinnedNewestFirst(props.events))
const activeEventIndex = ref(0)

const activeEvent = computed(() => sortedEvents.value[activeEventIndex.value])

const isGuestIdentity = (value?: string) =>
  Boolean(value && value.includes(GUEST) && !value.includes(PSTNGUEST))

const chatPServed = computed(() => props.pServed || '')

const room = computed(() => findByPServedRoom(chatPServed.value || activeEvent.value?.extendedParams?.sender || ''))

const guestDisplayName = computed(() => {
  const eventSender = activeEvent.value?.extendedParams?.sender
  const executor = activeEvent.value?.pServedUserExecuter

  if (!isGuestIdentity(executor) && !isGuestIdentity(eventSender)) {
    return undefined
  }

  const guestPServed = isGuestIdentity(executor) ? executor : eventSender
  const name = chatPServed.value && guestPServed
    ? findGuestByPserved(chatPServed.value, guestPServed)
    : undefined

  if (name) {
    return `${name} (${t('common.guest')})`
  }

  const roomGuests = chatPServed.value ? findByPServedRoom(chatPServed.value)?.guests : undefined
  if (roomGuests?.size === 1) {
    const [onlyGuest] = roomGuests.values()
    if (onlyGuest?.name) {
      return `${onlyGuest.name} (${t('common.guest')})`
    }
  }

  return t('common.guest')
})

const pinnedMessageSenderName = computed(() => {
  const eventSender = activeEvent.value?.extendedParams?.sender

  if (eventSender === currentUser.value?.imLogin) {
    return t('common.you')
  }

  const contact = findByPServed(eventSender ?? '')
    ?? findByPServed(activeEvent.value?.pServedUserExecuter ?? '')
  if (contact) {
    return contact.name
  }

  if (guestDisplayName.value) {
    return guestDisplayName.value
  }

  if (room.value) {
    return room.value.name
  }

  return eventSender ?? ''
})

const { findById } = chatModel.useChatMessageStore()

const voiceDurationSec = ref(0)
let voiceDurationRequestId = 0

const historyMessage = computed(() => {
  if (!activeEvent.value) {
    return undefined
  }
  return findById<MessageIncomingEvent | MessageOutgoingEvent>(activeEvent.value.eventId)
})

const resolveVoiceDuration = async () => {
  const requestId = ++voiceDurationRequestId
  voiceDurationSec.value = 0

  if (!activeEvent.value) {
    return
  }

  const fileCookie = getPinnedVoiceFileCookie(activeEvent.value, historyMessage.value)
  if (!fileCookie) {
    return
  }

  const cached = getCachedVoiceDurationSec(fileCookie)
  if (cached > 0) {
    voiceDurationSec.value = cached
    return
  }

  try {
    const { durationMs } = await getVoiceMessagePeaks(fileCookie)
    if (requestId !== voiceDurationRequestId) {
      return
    }
    voiceDurationSec.value = durationMs > 0 ? durationMs / 1000 : 0
  } catch {
    if (requestId === voiceDurationRequestId) {
      voiceDurationSec.value = 0
    }
  }
}

const preview = computed(() => {
  if (!activeEvent.value) {
    return { text: '', thumbnailUrl: undefined }
  }
  return getPinnedPreview(activeEvent.value, historyMessage.value, {
    voiceDurationSec: voiceDurationSec.value,
  })
})

const displayIndex = computed(() => sortedEvents.value.length - activeEventIndex.value)

const cycleToNext = () => {
  if (sortedEvents.value.length < 2) {
    return
  }

  activeEventIndex.value = (activeEventIndex.value + 1) % sortedEvents.value.length
}

/**
 * После клика по плашке держим «следующий» закреп и не слушаем viewport-sync,
 * пока пользователь сам не поскроллит ленту (иначе settle после goToMessage вернёт текущий пин).
 */
let isViewportSyncPaused = false

/**
 * Скролл ленты: показать закреп по позиции во viewport
 * (самый новый, ещё не ушедший целиком ниже нижней кромки).
 */
const setActiveByEventId = (eventId: number) => {
  if (isViewportSyncPaused) {
    return
  }

  const index = sortedEvents.value.findIndex((event) => event.eventId === eventId)
  if (index < 0 || index === activeEventIndex.value) {
    return
  }

  activeEventIndex.value = index
}

const resumeViewportSync = () => {
  isViewportSyncPaused = false
}

const onBarClick = () => {
  const eventId = activeEvent.value?.eventId
  if (!eventId) {
    return
  }

  emit('goToMessage', eventId)

  if (sortedEvents.value.length >= 2) {
    isViewportSyncPaused = true
    cycleToNext()
  }
}

defineExpose({
  setActiveByEventId,
  resumeViewportSync,
})

const onActionClick = () => {
  if (sortedEvents.value.length > 1) {
    const eventId = activeEvent.value?.eventId
    if (eventId) {
      emit('openList', eventId)
    }
    return
  }

  emit('deletePinEvent', activeEvent.value?.eventId ?? 0)
}

watch(
  () => sortedEvents.value.map((event) => event.eventId).join(','),
  (next, prev) => {
    if (next !== prev) {
      isViewportSyncPaused = false
      activeEventIndex.value = 0
    }
  },
)

watch(
  () => {
    const event = activeEvent.value
    if (!event) {
      return [0, undefined] as const
    }
    return [event.eventId, getPinnedVoiceFileCookie(event, historyMessage.value)] as const
  },
  () => {
    void resolveVoiceDuration()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  voiceDurationRequestId += 1
})
</script>
